/**
 * MissedCallEvent — value type for a missed call received by the local user.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/MissedCallEvent.swift`.
 *
 * ## Security invariant: caller display hint is ALWAYS locally derived.
 *
 * Even if the incoming signal payload carries a `from_name` or similar
 * field, that byte is DISCARDED. The `callerDisplayHint` is derived
 * exclusively from the local contact roster supplied by the caller of
 * `makeMissedCallEvent`. A non-roster peer yields the generic string
 * `"Unknown caller"` — the server can never inject an arbitrary display
 * name into a missed-call notification.
 *
 * This mirrors Signal's design choice to keep display names
 * client-authoritative and prevents a rogue server from making
 * "system@kaether.fr" appear as a notification body.
 */

// ---------------------------------------------------------------------------
// Roster interface (caller supplies this; we never touch the network here)
// ---------------------------------------------------------------------------

/**
 * Minimal read-only view of the local contact roster that MissedCallEvent
 * needs. The full roster type lives elsewhere in the client; this interface
 * keeps the missed-call module decoupled from it.
 */
export interface RosterLookup {
  /** Returns the locally-stored display name for `userId`, or `undefined`
   *  if the user is not in the local roster. */
  displayName(userId: string): string | undefined;
}

// ---------------------------------------------------------------------------
// The value type
// ---------------------------------------------------------------------------

/**
 * Immutable snapshot of a single missed call.
 *
 * All fields are set at construction time and never mutated. The object can
 * be serialised to/from JSON for IndexedDB storage.
 */
export interface MissedCallEvent {
  /** Stable identifier for this call. Used for dedup in MissedCallStore. */
  readonly callId: string;
  /** The thread (1:1 or group) in which the call occurred. */
  readonly threadId: string;
  /** UserId of the caller (as assigned by the server / SignalProtocol). */
  readonly callerUserId: string;
  /**
   * Display hint derived from the LOCAL roster. NEVER from server-claimed
   * strings in the call payload. If the caller is not in the roster,
   * this is `UNKNOWN_CALLER_HINT`.
   */
  readonly callerDisplayHint: string;
  /** Unix-epoch milliseconds when the call was received. */
  readonly receivedAtMs: number;
}

/** Sentinel returned when the caller is not found in the local roster. */
export const UNKNOWN_CALLER_HINT = "Unknown caller";

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Construct a `MissedCallEvent`.
 *
 * `serverPayload` may carry whatever the server sends (including a
 * `from_name` field). The factory intentionally ignores all display-name
 * fields in `serverPayload` and derives `callerDisplayHint` solely from
 * `roster.displayName(callerUserId)`.
 *
 * @param callId        - Stable call identifier (from signalling layer).
 * @param threadId      - Thread in which the call occurred.
 * @param callerUserId  - UserId of the caller (from signalling layer).
 * @param receivedAtMs  - Unix-epoch ms of receipt (default: Date.now()).
 * @param roster        - Local roster used to derive the display hint.
 * @param _serverPayload - Intentionally unused; present so callers can pass
 *                         the raw server payload without needing to strip it.
 */
export function makeMissedCallEvent(
  callId: string,
  threadId: string,
  callerUserId: string,
  roster: RosterLookup,
  receivedAtMs: number = Date.now(),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _serverPayload?: Record<string, unknown>,
): MissedCallEvent {
  // Derive display hint from local roster only — server payload is ignored.
  const callerDisplayHint =
    roster.displayName(callerUserId) ?? UNKNOWN_CALLER_HINT;

  return Object.freeze({
    callId,
    threadId,
    callerUserId,
    callerDisplayHint,
    receivedAtMs,
  });
}
